import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
  Input,
  ViewChild
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

import { CommonDropdownControlDirective } from '../../directives/control/common-dropdown-control.directive';

@Component({
  selector: 'kh-common-dropdown',
  standalone: true,
  imports: [CommonModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CommonDropdownComponent),
      multi: true
    }
  ],
  templateUrl: './common-dropdown.component.html',
  styleUrls: ['./common-dropdown.component.less']
})
export class CommonDropdownComponent<TItem, TValue> extends CommonDropdownControlDirective<TItem, TValue> {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  @Input() public ariaLabel = '';

  @ViewChild('toggleButton') private readonly toggleButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('dropdownMenu') private readonly dropdownMenu?: ElementRef<HTMLElement>;

  public isOpen = false;

  public toggle(): void {
    if (this.isOpen) {
      this.close();

      return;
    }

    this.open();
  }

  public open(focusLastItem = false): void {
    if (this.disabled) {
      return;
    }

    const button = this.toggleButton?.nativeElement;
    const menu = this.dropdownMenu?.nativeElement;

    if (!button || !menu) {
      return;
    }

    this.positionDropdown(button, menu);

    if (!menu.matches(':popover-open')) {
      menu.showPopover();
    }

    this.isOpen = true;

    setTimeout(() => this.focusMenuItem(focusLastItem));
  }

  public close(returnFocus = false): void {
    const menu = this.dropdownMenu?.nativeElement;

    if (this.isOpen) {
      this.onBlur();
    }

    if (menu?.matches(':popover-open')) {
      menu.hidePopover();
    }

    this.isOpen = false;

    if (returnFocus) {
      this.toggleButton?.nativeElement.focus();
    }
  }

  public override onItemClick(item: TItem): void {
    super.onItemClick(item);

    this.close(true);
  }

  public isSelected(item: TItem): boolean {
    return this.getValue(item) === this.model;
  }

  public onToggleArrowKey(event: KeyboardEvent, focusLastItem: boolean): void {
    event.preventDefault();

    if (!this.isOpen) {
      this.open(focusLastItem);

      return;
    }

    this.focusMenuItem(focusLastItem);
  }

  public onMenuKeydown(event: KeyboardEvent): void {
    const menuItems = this.getMenuItems();

    if (!menuItems.length) {
      return;
    }

    const currentIndex = menuItems.indexOf(document.activeElement as HTMLElement);

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        this.close(true);
        break;

      case 'Tab':
        this.close();
        break;

      case 'ArrowDown':
        event.preventDefault();
        menuItems[
          currentIndex === -1
            ? 0
            : (currentIndex + 1) % menuItems.length
        ]?.focus();
        break;

      case 'ArrowUp':
        event.preventDefault();
        menuItems[
          currentIndex === -1
            ? menuItems.length - 1
            : (currentIndex - 1 + menuItems.length) % menuItems.length
        ]?.focus();
        break;

      default:
        break;
    }
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    if (
      this.isOpen
      && !this.elementRef.nativeElement.contains(event.target as Node)
    ) {
      this.close();
    }
  }

  @HostListener('window:resize')
  public onWindowResize(): void {
    if (!this.isOpen) {
      return;
    }

    this.updateDropdownPosition();
  }

  @HostListener('window:scroll')
  public onWindowScroll(): void {
    if (!this.isOpen) {
      return;
    }

    this.updateDropdownPosition();
  }

  private updateDropdownPosition(): void {
    const button = this.toggleButton?.nativeElement;
    const menu = this.dropdownMenu?.nativeElement;

    if (!button || !menu) {
      return;
    }

    this.positionDropdown(button, menu);
  }

  private positionDropdown(
    button: HTMLButtonElement,
    menu: HTMLElement
  ): void {
    const rect = button.getBoundingClientRect();

    menu.style.setProperty(
      '--dropdown-top',
      `${rect.bottom + 4}px`
    );

    menu.style.setProperty(
      '--dropdown-left',
      `${rect.left}px`
    );

    menu.style.setProperty(
      '--dropdown-width',
      `${rect.width}px`
    );
  }

  private getMenuItems(): HTMLElement[] {
    return Array.from(
      this.dropdownMenu?.nativeElement.querySelectorAll<HTMLElement>(
        '.dropdown-option'
      ) ?? []
    );
  }

  private focusMenuItem(focusLastItem: boolean): void {
    const menuItems = this.getMenuItems();

    (
      focusLastItem
        ? menuItems[menuItems.length - 1]
        : menuItems[0]
    )?.focus();
  }
}
